import React, { useState } from "react"
import styled from "styled-components"
import { TaskAlt, Lock, Close, InfoOutlined } from "@mui/icons-material"
import { MobergFontFamily } from "../../../Components/MobergFont/MobergFont"
import { MobergButton, MobergButtonVariant, MobergButtonShape, MobergRow } from "../../../Moberg"
import { getColors, MobergTheme } from "../../../Components/MobergThemes/MobergColors"
import { ScreenshotEntry } from "../../../Providers/ScreenshotsProvider"
import { useMobergTheme } from "../../../Providers/MobergThemeProvider"
import { useTableContext } from "../../../Providers/TableProvider"
import { useModalProvider } from "../../../Providers/ModalProvider"


type ScreenshotExportDialogProps = {
	screenshot: ScreenshotEntry
	uploadEndpoint: string
	onExport: (deidentify: boolean) => Promise<any>
	onClose: () => void
}


// ─── Main Dialog ──────────────────────────────────────────────────────────────

function ScreenshotExportDialog({ onExport, onClose }: ScreenshotExportDialogProps): JSX.Element {
	return (
		<DialogContainer>
			<DialogHeader>
				<div style={{ flex: 1 }}>
					<DialogTitle>Take screenshot</DialogTitle>
				</div>
				<CloseButton onClick={onClose}><Close /></CloseButton>
			</DialogHeader>

			<CardGrid>
				<ExportCard
					title="Original"
					desc="Includes all patient identifiable information"
					deidentify={false}
					onExport={onExport}
				/>
				<ExportCard
					title="De-identified"
					desc="Removes patient name, ID, and metadata"
					deidentify={true}
					onExport={onExport}
				/>
			</CardGrid>
			<MobergRow gap="8px" style={{ padding: "16px", paddingTop: 0 }}>
				<Lock sx={{ fontSize: 16, color: "#6b7280" }} />
				<FooterText>Screenshots are not saved to your device. They will be permanently deleted when the session ends.</FooterText>
			</MobergRow>
		</DialogContainer>
	)
}

// ─── Subcomponents (unchanged from original) ──────────────────────────────────

type ExportButtonProps = {
	deidentify: boolean
	onExport: (deidentify: boolean) => Promise<any>
}

function ExportButton({ deidentify, onExport }: ExportButtonProps): JSX.Element {
	const { close } = useModalProvider()
	const [isTakingScreenshot, setIsTakingScreenshot] = useState(false)
	const { setToastProps } = useTableContext()
	const onClick = async () => {
		setIsTakingScreenshot(true)
		
		// Wait for the state to update to show that the button was clicked, then do the task
		setTimeout(async () => {
			let severity, message

			try {
				await onExport(deidentify)
				message = "Screenshot saved."
				severity = "success"
			} catch (error) {
				message = `${error}`
				severity = "error"
			} finally {
				close()
			}

			setToastProps({isOpen: true, message, severity, autoHideDurationMs: 2000 })
		}, 0)
	}

	return (
		<MobergButton
			theme={deidentify ? MobergTheme.BLUE : MobergTheme.BLACK}
			variant={MobergButtonVariant.FILLED}
			shape={MobergButtonShape.RECTANGLE}
			disabled={isTakingScreenshot}
			onClick={onClick}
		>
			Take screenshot
		</MobergButton>
	)
}

type ExportCardProps = {
	title: string
	desc: string
	deidentify: boolean
	onExport: (deidentify: boolean) => Promise<any>
}

function ExportCard({ title, desc, deidentify, onExport }: ExportCardProps): JSX.Element {
	const { colorScheme } = useMobergTheme()

	return (
		<Card deidentify={deidentify} colorScheme={colorScheme}>
			<CardTopRow>
				<CardIcon deidentify={deidentify}>
					{deidentify ? <TaskAlt sx={{ fontSize: 42 }} /> : <InfoOutlined sx={{ fontSize: 42 }} />}
				</CardIcon>
				<CardHeader>
					<CardTitle>{title}</CardTitle>
				</CardHeader>
			</CardTopRow>
			<CardDescription>{desc}</CardDescription>
			<ExportButton deidentify={deidentify} onExport={onExport} />
		</Card>
	)
}

// ─── Styled Components ────────────────────────────────────────────────────────

const DialogContainer = styled.div`
	border-radius: 4px;
	min-width: 650px;
	max-width: 90vw;
`

const DialogHeader = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: flex-start;
	padding: 24px 24px 16px 24px;
`

const DialogTitle = styled.h2`
	font-family: ${MobergFontFamily.HEADER};
	font-size: 20px;
	font-weight: 600;
	margin: 0 0 5px 0;
`

const CloseButton = styled.button`
	background: none;
	border: none;
	color: #6b7280;
	cursor: pointer;
	padding: 4px;
	border-radius: 4px;
	display: flex;
	align-items: center;
	justify-content: center;
	transition: all 0.2s;
	&:hover { background: #f3f4f6; color: #374151; }
`

const CardGrid = styled.div`
	display: grid;
	grid-template-columns: 1fr 1fr;
	gap: 16px;
	padding: 8px 24px 16px 24px;
`

const Card = styled.div<{ deidentify: boolean, colorScheme: "light" | "dark" }>`
	background: ${({ deidentify, colorScheme }) => (
		deidentify 
			? colorScheme === "dark" ? getColors(MobergTheme.BLUE).dark?.hover : getColors(MobergTheme.BLUE).noFill.hover 
			: colorScheme === "dark" ? "#090909" : "#f8fafc")
	};
	border-radius: 4px;
	padding: 18px;
	display: flex;
	flex-direction: column;
	align-items: center;
`

const CardTopRow = styled.div`
	display: flex;
	align-items: center;
	gap: 12px;
	width: 100%;
	margin-bottom: 18px;
`

const CardIcon = styled.div<{ deidentify: boolean }>`
	color: ${({ deidentify }) => deidentify ? getColors(MobergTheme.BLUE).main : "var(--color-text-primary)"};
	display: flex;
	flex-shrink: 0;
`

const CardHeader = styled.div`
	display: flex;
	flex-direction: column;
	gap: 2px;
`

const CardTitle = styled.span`
	font-family: ${MobergFontFamily.HEADER};
	font-size: 18px;
	font-weight: 600;
	color: var(--color-text-primary);
`

const CardDescription = styled.p`
	font-family: ${MobergFontFamily.REGULAR};
	font-size: 16px;
	color: var(--color-text-primary);
	margin: 0 0 18px 0;
	line-height: 1.4;
	width: 100%;
	text-align: left;
	flex-grow: 1;
`

const FooterText = styled.span`
	font-family: ${MobergFontFamily.REGULAR};
	font-size: 14px;
	color: #6b7280;
`

export default ScreenshotExportDialog